// Shared edge session HTTP endpoints (issue #1100).
//
// The refresh/logout calls and the logout-reason normalization were previously
// duplicated near-verbatim in each app's api/edge-session.ts. Factoring them here
// means a server contract change (new reason, different error shape, endpoint
// path) is edited once. Each app supplies only its host + token accessors.

import { LOGOUT_REASON_HEADER, LogoutReason } from "./policy";

// How long to wait for the best-effort logout/revoke before aborting it, so a
// slow auth container can't wedge sign-out (#1100).
const REVOKE_TIMEOUT_MS = 3000;
// How long a refresh may take before it is aborted and retried (#1100).
const REFRESH_TIMEOUT_MS = 15000;
// Attempts at the logout call when the server couldn't record the revoke (5xx)
// or the request failed, all within REVOKE_TIMEOUT_MS.
const REVOKE_ATTEMPTS = 3;
const REVOKE_RETRY_DELAY_MS = 500;

export interface EdgeSessionEndpointsConfig {
  /** Base auth host (already scheme+host, no trailing slash), e.g. https://.../auth. */
  authHost: () => string | undefined;
  /** Current access token, or null. */
  getToken: () => string | null;
  /**
   * Extra init merged into every fetch. The editor uses this to mark its own
   * refresh/logout calls (`__staleRetried`) so its window.fetch interceptor
   * ignores them; the web-app leaves it unset (its interceptor is axios).
   */
  fetchInit?: Record<string, unknown>;
}

/**
 * A successful refresh. The durations are measured on the server clock and are
 * what the manager schedules from — never the token's exp / login_at timestamps,
 * which would have to be compared with this browser's own, possibly skewed, clock
 * (#1100). Null when the server didn't send them (an older auth service).
 */
export interface RefreshResult {
  token: string;
  /** How long the new token is valid for, in ms. */
  expiresInMs: number | null;
  /** How long until the session's 12h absolute ceiling, in ms. */
  absoluteInMs: number | null;
}

export interface EdgeSessionEndpoints {
  /**
   * Resolve with the new token, or reject with `{ reason }` the manager reads.
   * idleMs is how long the user has been inactive (a duration in ms, so it carries
   * no client/server clock skew); a negative value means "unknown — let the server
   * decide from its own stored last_activity" (#1100).
   */
  refresh: (idleMs?: number) => Promise<RefreshResult>;
  /**
   * Best-effort server revoke; never throws. The optional reason is recorded in
   * the server's session telemetry (defaults to a manual sign-out) (#1100).
   */
  revoke: (reason?: LogoutReason) => Promise<void>;
}

export function createEdgeSessionEndpoints(
  cfg: EdgeSessionEndpointsConfig,
): EdgeSessionEndpoints {
  const extra = cfg.fetchInit ?? {};

  const refresh = async (idleMs?: number): Promise<RefreshResult> => {
    const token = cfg.getToken();
    if (!token) throw { reason: "token-expired" as LogoutReason };
    // Bound the whole request. The manager runs one refresh at a time, so a
    // request the auth service never answered would block every later renewal
    // until the token expired and the user was logged out. A timeout aborts it;
    // the abort reaches the manager without a reason, i.e. as a transient
    // failure it retries (#1100).
    const controller =
      typeof AbortController !== "undefined"
        ? new AbortController()
        : undefined;
    const timer = controller
      ? setTimeout(() => controller.abort(), REFRESH_TIMEOUT_MS)
      : undefined;
    try {
      return await refreshOnce(token, idleMs, controller);
    } finally {
      if (timer) clearTimeout(timer);
    }
  };

  const refreshOnce = async (
    token: string,
    idleMs: number | undefined,
    controller: AbortController | undefined,
  ): Promise<RefreshResult> => {
    const res = await fetch(`${cfg.authHost()}/users/edge/refresh`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      // Report how long the user has been idle (a duration, so no clock skew) so
      // the server enforces idle against real activity, not the refresh cadence
      // (#1100). A negative value means "unknown" — the server falls back to its
      // own stored last_activity.
      body: JSON.stringify({ idle_ms: idleMs ?? -1 }),
      mode: "cors",
      signal: controller ? controller.signal : undefined,
      ...extra,
    });
    if (!res.ok) {
      // Only an explicit 401/403 is a terminal, server-decided end of the session
      // (thrown as `{ reason }` so the manager logs out). A 5xx / 503 is transient
      // and thrown WITHOUT a reason, so the manager retries instead of logging the
      // user out over a blip (#1100). A network error rejects `fetch` before here
      // and likewise reaches the manager without a reason.
      if (res.status === 401 || res.status === 403) {
        const body = unwrap(await res.json().catch((): null => null));
        const reason =
          res.headers.get(LOGOUT_REASON_HEADER) ||
          (typeof body.reason === "string" ? body.reason : "") ||
          "token-expired";
        throw { reason };
      }
      throw new Error(`edge session refresh failed: ${res.status}`);
    }
    const body = unwrap(await res.json().catch((): null => null));
    const newToken =
      typeof body.userIdToken === "string" ? body.userIdToken : "";
    if (!newToken) throw { reason: "token-expired" as LogoutReason };
    return {
      token: newToken,
      expiresInMs: durationField(body.expires_in_ms),
      absoluteInMs: durationField(body.absolute_in_ms),
    };
  };

  const revoke = async (reason?: LogoutReason): Promise<void> => {
    const token = cfg.getToken();
    if (!token) return;
    // Abort the revoke if the auth container is slow so it can't wedge the
    // caller; local logout proceeds regardless (#1100). The manager also races a
    // timeout, but aborting frees the request rather than leaving it dangling.
    const controller =
      typeof AbortController !== "undefined"
        ? new AbortController()
        : undefined;
    const timer = controller
      ? setTimeout(() => controller.abort(), REVOKE_TIMEOUT_MS)
      : undefined;
    // The server answers 5xx when it could not record the revoke; a session left
    // live would keep working in other tabs/devices until its exp. Retry a few
    // times inside the same REVOKE_TIMEOUT_MS window (the abort ends the loop),
    // then give up quietly: local logout proceeds regardless (#1100).
    try {
      for (let attempt = 0; attempt < REVOKE_ATTEMPTS; attempt++) {
        if (attempt > 0) {
          await new Promise<void>((resolve) =>
            setTimeout(resolve, REVOKE_RETRY_DELAY_MS),
          );
        }
        if (controller && controller.signal.aborted) return;
        try {
          const res = await fetch(`${cfg.authHost()}/users/edge/logout`, {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            // Carry the real reason (idle / absolute / manual) so the server
            // records the actual logout cause rather than assuming a manual
            // sign-out (#1100).
            body: JSON.stringify({ reason: reason ?? "manual" }),
            mode: "cors",
            signal: controller ? controller.signal : undefined,
            ...extra,
          });
          if (res.status < 500) return; // done, or nothing a retry would fix
        } catch {
          if (controller && controller.signal.aborted) return; // timed out
          /* network error: retry */
        }
      }
    } finally {
      if (timer) clearTimeout(timer);
    }
  };

  return { refresh, revoke };
}

/**
 * The Go auth service wraps every JSON reply as `{"data": {...}}` (utils.
 * RespondWithJSON). Reading fields off the top level found nothing, so every
 * refresh "failed" and logged the user out about 20 minutes in (#1100). Returns
 * the inner object, or an empty one for anything unexpected; written with plain
 * guards so it reads the same in any build target.
 */
function unwrap(json: unknown): Record<string, unknown> {
  if (!json || typeof json !== "object") return {};
  const data = (json as { data?: unknown }).data;
  if (data && typeof data === "object") return data as Record<string, unknown>;
  return {};
}

function durationField(v: unknown): number | null {
  return typeof v === "number" && isFinite(v) && v >= 0 ? v : null;
}

/** Coerce an arbitrary server-supplied string into a known LogoutReason. */
export function normalizeLogoutReason(
  reason: string | null | undefined,
): LogoutReason {
  const known: LogoutReason[] = [
    "idle",
    "absolute",
    "token-expired",
    "manual",
    "revoked",
  ];
  return known.includes(reason as LogoutReason)
    ? (reason as LogoutReason)
    : "token-expired";
}
